import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { useQuery } from "@apollo/client";
import { MUTATION_TYPE, useToast } from "hooks";
import { ADD_MISSION_ROLE_TEMPLATE, UPDATE_MISSION_ROLE_TEMPLATE } from "graphql/mutations";
import { GET_DISCIPLINES, GET_DOMAINS, GET_SKILLS } from "graphql/queries";
import { DOMAIN_NAMES, getBlueprintFormValues } from "constants/index";
import { ModalForm } from "components/Modal";
import { SIZE, TYPE } from "components/Form";

/**
 * BlueprintEditModal
 *
 * Names and tags a blueprint; its vetting modules are edited on the blueprint's own page.
 *
 * @param {Object}    template        The blueprint to edit; leave out to add one
 * @param {Boolean}   show
 * @param {Function}  onClose
 * @param {Array}     refetchQueries  Re-read after saving, such as the blueprints list
 */
const BlueprintEditModal = ({ template, show, onClose, refetchQueries = [], ...props }) => {
  const { toast } = useToast();
  const { data: domainsData } = useQuery(GET_DOMAINS);
  const isEditMode = Boolean(template?.uuid);
  const mode = isEditMode ? "Edit" : "Add";
  const domainId = domainsData?.domains?.nodes?.find(({ name }) => name === DOMAIN_NAMES.talentSimple)?.id;

  const handleCompleted = () => {
    onClose();
    toast.success(<FormattedMessage id={`Blueprints.EditModal.${mode}Success`} />);
  };

  const handleError = () => toast.error(<FormattedMessage id={`Blueprints.EditModal.${mode}Error`} />);

  return (
    <ModalForm
      title={<FormattedMessage id={`Blueprints.EditModal.${mode}Title`} />}
      show={show}
      onClose={onClose}
      data={COLUMN_DATA({ domainId })}
      initialValues={getBlueprintFormValues(template)}
      updateId={template?.uuid}
      mutationType={isEditMode ? MUTATION_TYPE.update : MUTATION_TYPE.add}
      mutationData={{
        mutationName: {
          update: UPDATE_MISSION_ROLE_TEMPLATE,
          add: ADD_MISSION_ROLE_TEMPLATE,
        },
        refetchAfterMutate: refetchQueries.length > 0,
        refetchQueries,
      }}
      onCompleted={handleCompleted}
      onError={handleError}
      {...props}
    />
  );
};

const COLUMN_DATA = ({ domainId }) => [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        label: "Name",
        type: TYPE.input,
        size: SIZE.xlarge,
        properties: {
          name: "name",
          type: "text",
          required: true,
          autoFocus: true,
          maxLength: 100,
        },
      },
      {
        label: "Disciplines",
        type: TYPE.tags,
        size: SIZE.xlarge,
        properties: {
          name: "disciplines",
          placeholder: "Type a discipline...",
          queryName: GET_DISCIPLINES,
          queryKeyName: "disciplines",
          queryVariables: { domainId },
          minLength: 0,
          required: false,
        },
      },
      {
        label: "Skills",
        type: TYPE.tags,
        size: SIZE.xlarge,
        properties: {
          name: "primarySkills",
          placeholder: "Type a skill...",
          queryName: GET_SKILLS,
          queryKeyName: "skills",
          queryVariables: { domainId },
          minLength: 1,
          required: false,
        },
      },
    ],
  },
];

BlueprintEditModal.propTypes = {
  template: PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    disciplines: PropTypes.array,
    primarySkills: PropTypes.array,
  }),
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchQueries: PropTypes.array,
};

export default BlueprintEditModal;
